import { useState, useEffect } from "react";
import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { LayoutDashboard, Scissors, Users, LogOut, ShoppingCart, Package, Image, ClipboardList, MessageCircle, Briefcase, RotateCcw, Settings, DollarSign, FileText, UserCheck, Star, Code, Menu, X, BarChart3 } from "lucide-react";

const LOGO = "https://customer-assets.emergentagent.com/job_0890dc80-6146-4c2d-b11f-fa0b2de6d70b/artifacts/ekzdqal8_image.png";

const allNavItems = [
  { to: "/admin", icon: LayoutDashboard, label: "Dashboard", end: true, roles: ["administrator", "kepala_cabang", "finance", "store_manager"] },
  { to: "/admin/finance", icon: DollarSign, label: "Keuangan", roles: ["administrator", "kepala_cabang", "finance"] },
  { to: "/admin/daily-report", icon: FileText, label: "Laporan Harian", roles: ["administrator", "store_manager", "kepala_cabang", "finance"] },
  { to: "/admin/statistics", icon: BarChart3, label: "Statistik", roles: ["administrator", "kepala_cabang", "store_manager", "finance"] },
  { to: "/admin/services", icon: Scissors, label: "Layanan", roles: ["administrator", "kepala_cabang", "store_manager"] },
  { to: "/admin/products", icon: Package, label: "Produk", roles: ["administrator", "kepala_cabang", "store_manager"] },
  { to: "/admin/orders", icon: ClipboardList, label: "Pesanan", roles: ["administrator", "kepala_cabang", "store_manager"] },
  { to: "/admin/employees", icon: Users, label: "Karyawan", roles: ["administrator", "kepala_cabang"] },
  { to: "/admin/customers", icon: UserCheck, label: "Pelanggan", roles: ["administrator", "kepala_cabang", "store_manager"] },
  { to: "/admin/gallery", icon: Image, label: "Galeri", roles: ["administrator", "store_manager"] },
  { to: "/admin/whatsapp", icon: MessageCircle, label: "WhatsApp", roles: ["administrator", "store_manager"] },
  { to: "/admin/recruitment", icon: Briefcase, label: "Rekrutmen", roles: ["administrator", "store_manager"] },
  { to: "/admin/testimonial", icon: Star, label: "Testimonial", roles: ["administrator", "store_manager", "kepala_cabang"] },
  { to: "/admin/refund", icon: RotateCcw, label: "Refund", roles: ["administrator", "kepala_cabang", "finance", "store_manager"] },
  { to: "/admin/settings", icon: Settings, label: "Pengaturan", roles: ["administrator"] },
  { to: "/admin/developer", icon: Code, label: "Developer", roles: ["administrator"] },
  { to: "/pos", icon: ShoppingCart, label: "POS Kasir", external: true, roles: ["administrator", "kepala_cabang"] },
];

const roleLabels = {
  administrator: "Administrator",
  kepala_cabang: "Kepala Cabang",
  finance: "Finance",
  store_manager: "Store Manager",
};

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Auto-close sidebar on route change (mobile)
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  // Lock body scroll when mobile sidebar is open
  useEffect(() => {
    if (sidebarOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [sidebarOpen]);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const navItems = allNavItems.filter(item => item.roles.includes(user?.role));

  const currentItem = navItems.find(i => i.to === location.pathname) || navItems.find(i => location.pathname.startsWith(i.to) && i.to !== "/admin");
  const currentLabel = currentItem?.label || "Admin";

  return (
    <div className="flex h-screen bg-[#050505] overflow-hidden" data-testid="admin-layout">
      {/* Mobile backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 md:hidden"
          onClick={() => setSidebarOpen(false)}
          data-testid="sidebar-backdrop"
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed md:static top-0 left-0 h-full w-64 md:w-60 bg-[#0F0F0F] border-r border-[#262626] flex flex-col shrink-0 z-50 transform transition-transform duration-300 ease-in-out ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        } md:translate-x-0`}
        data-testid="admin-sidebar"
      >
        <div className="p-5 border-b border-[#262626] flex items-center gap-3 justify-between">
          <div className="flex items-center gap-3 min-w-0">
            <img src={LOGO} alt="FI" className="w-9 h-9 rounded-full shrink-0" />
            <div className="min-w-0">
              <p className="font-heading text-sm font-bold uppercase tracking-wider text-white truncate">FI Barbershop</p>
              <p className="text-[10px] text-gray-500 uppercase tracking-wider">Admin Panel</p>
            </div>
          </div>
          <button
            onClick={() => setSidebarOpen(false)}
            className="md:hidden text-gray-400 hover:text-white p-1"
            data-testid="sidebar-close-btn"
            aria-label="Close sidebar"
          >
            <X size={20} />
          </button>
        </div>
        <nav className="flex-1 p-3 space-y-1 overflow-auto" data-testid="admin-nav">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 text-sm font-semibold uppercase tracking-wider transition-colors ${
                  isActive && !item.external ? "bg-[#E60000]/10 text-[#E60000] border-l-2 border-[#E60000]" : "text-gray-400 hover:text-white hover:bg-[#1A1A1A]"
                }`
              }
            >
              <item.icon size={18} />
              {item.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-[#262626]">
          <div className="px-3 py-2 mb-2">
            <p className="text-xs text-gray-500 uppercase tracking-wider">Masuk sebagai</p>
            <p className="text-sm text-white font-semibold truncate">{user?.name}</p>
            <p className="text-xs text-gray-500">{roleLabels[user?.role] || user?.role}</p>
          </div>
          <button onClick={handleLogout} className="flex items-center gap-3 px-3 py-2.5 text-sm font-semibold uppercase tracking-wider text-gray-400 hover:text-[#E60000] hover:bg-[#1A1A1A] w-full transition-colors" data-testid="logout-btn">
            <LogOut size={18} /> Keluar
          </button>
        </div>
      </aside>

      {/* Main area */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Mobile top bar */}
        <header className="md:hidden sticky top-0 z-30 bg-[#0F0F0F] border-b border-[#262626] flex items-center justify-between px-4 py-3" data-testid="admin-topbar">
          <button
            onClick={() => setSidebarOpen(true)}
            className="text-gray-300 hover:text-white p-1"
            data-testid="sidebar-open-btn"
            aria-label="Open sidebar"
          >
            <Menu size={22} />
          </button>
          <div className="flex items-center gap-2 min-w-0">
            <img src={LOGO} alt="FI" className="w-7 h-7 rounded-full shrink-0" />
            <p className="font-heading text-sm font-bold uppercase tracking-wider text-white truncate">{currentLabel}</p>
          </div>
          <div className="w-8" />
        </header>

        <main className="flex-1 overflow-auto" data-testid="admin-main">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
